import { useState } from "preact/hooks";
import { isE164, normalizePhone } from "../lib/phone";
import type { VisitorInfo } from "../types";

interface PreChatFormProps {
  initial?: VisitorInfo;
  submitting?: boolean;
  error?: string | null;
  onSubmit: (info: VisitorInfo) => void;
}

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export function PreChatForm({
  initial,
  submitting,
  error,
  onSubmit,
}: PreChatFormProps) {
  const [name, setName] = useState(initial?.name ?? "");
  const [email, setEmail] = useState(initial?.email ?? "");
  const [phone, setPhone] = useState(initial?.phone ?? "");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = (): VisitorInfo | null => {
    const next: Record<string, string> = {};
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const normalized = normalizePhone(phone);

    if (!trimmedName) next.name = "Your name is required.";
    if (!trimmedEmail) next.email = "Email is required.";
    else if (!EMAIL_RE.test(trimmedEmail))
      next.email = "Please enter a valid email.";
    if (normalized && !isE164(normalized)) {
      next.phone =
        "Please enter a valid phone number — e.g. (888) 450-2020 or +44 20 7946 0958.";
    }

    setErrors(next);
    if (Object.keys(next).length) return null;
    return {
      name: trimmedName,
      email: trimmedEmail.toLowerCase(),
      phone: normalized || undefined,
    };
  };

  const handleSubmit = (e: Event) => {
    e.preventDefault();
    if (submitting) return;
    const info = validate();
    if (info) onSubmit(info);
  };

  return (
    <form class="nx-form" onSubmit={handleSubmit} noValidate>
      <p class="nx-form__intro">
        Tell us a bit about yourself so we can follow up if we get disconnected.
      </p>

      <label class="nx-field">
        <span class="nx-field__label">Name</span>
        <input
          class="nx-field__input"
          type="text"
          autoComplete="name"
          value={name}
          onInput={(e) => setName((e.target as HTMLInputElement).value)}
          disabled={submitting}
          required
        />
        {errors.name && <span class="nx-field__error">{errors.name}</span>}
      </label>

      <label class="nx-field">
        <span class="nx-field__label">Email</span>
        <input
          class="nx-field__input"
          type="email"
          autoComplete="email"
          value={email}
          onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
          disabled={submitting}
          required
        />
        {errors.email && <span class="nx-field__error">{errors.email}</span>}
      </label>

      <label class="nx-field">
        <span class="nx-field__label">
          Phone <span class="nx-field__hint">(optional)</span>
        </span>
        <input
          class="nx-field__input"
          type="tel"
          autoComplete="tel"
          placeholder="(888) 450-2020"
          value={phone}
          onInput={(e) => setPhone((e.target as HTMLInputElement).value)}
          disabled={submitting}
        />
        {errors.phone && <span class="nx-field__error">{errors.phone}</span>}
      </label>

      {error && <div class="nx-form__error">{error}</div>}

      <button class="nx-form__submit" type="submit" disabled={submitting}>
        {submitting ? "Connecting…" : "Start chat"}
      </button>
    </form>
  );
}
